import React from 'react';

import { Select } from '@aline/common';
import { Skeleton, Typography } from '@mui/material';
import { omit } from 'ramda';
import { EditableNameInput, EditableNameVariant } from 'src/components';
import { AlineButton } from 'src/components/generic';
import type { Apollo } from 'src/gql/generated';
import type { CreateWorkflowInstanceArgs } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { AddDocuments } from './AddDocuments';
import { AssignRoles } from './AssignRoles';
import {
  getLaunchDocumentFields,
  getShouldShowDocumentsSection,
} from './launch-template-visibility';
import { LaunchDocumentFieldsStep } from './LaunchDocumentFieldsStep';
import { LaunchIntakeFormStep } from './LaunchIntakeFormStep';
import { PickIntegrationOpportunity } from './PickIntegrationOportunity';
import { TemplateWorkflowsList } from './TemplateWorkflowsList';
import { StepLayout } from '../StepLayout';

export type TemplateQuickStartState = Omit<CreateWorkflowInstanceArgs, 'templateWorkflowId'> & {
  excludedDocumentIds: UUID[];
};

export type OnValuesChangePayload = Partial<
  Pick<
    CreateWorkflowInstanceArgs,
    | 'alineFieldValues'
    | 'integrationRecordMap'
    | 'roleAssignmentMap'
    | 'templateIdToContractName'
    | 'workflowName'
    | 'moreDocumentIds'
    | 'replacementFiles'
    | 'intakeFormValues'
  > & {
    excludedDocumentIds?: UUID[];
  }
>;

/**
 * Accepts a plain patch or a functional updater. The updater form lets callers
 * merge against the latest state (e.g. an async file upload completing after an
 * unrelated field changed) instead of a render-stale snapshot.
 */
export type OnValuesChange = (
  values: OnValuesChangePayload | ((prev: TemplateQuickStartState) => OnValuesChangePayload)
) => void;

export type LaunchTemplateWorkflow = NonNullable<Apollo.LaunchTemplateDataQuery['workflow']>;

export const useBodyStyles = makeStyles<{ isTemplateSelected: boolean }>()(
  (theme, { isTemplateSelected }) => ({
    main: {
      backgroundColor: theme.appColors.background,
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(2),
    },
    noTemplates: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    stepsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(4),
    },
    template: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
        border: `1px solid ${theme.palette.primary.main}`,
      },
      backgroundColor: isTemplateSelected ? theme.appColors.hoverBackground : undefined,
      border: isTemplateSelected
        ? `1px solid ${theme.palette.primary.main}`
        : `1px solid ${theme.appColors.rule}`,
      borderRadius: 16,
      boxSizing: 'border-box',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    },
    templateTextContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    workflowNameInput: {
      width: 'fit-content',
    },
  })
);

export function LaunchTemplateBody({
  activeStep,
  connectionsToShow,
  loadError,
  loading,
  onRetry,
  onValuesChange,
  preloadedOpportunityNames,
  quickStartState,
  showRequiredErrorsToken,
  templateWorkflow,
  templateWorkflowId,
}: {
  activeStep: number;
  connectionsToShow: Apollo.ApiDeckConnectionSimpleFragment[];
  loadError: boolean;
  loading: boolean;
  onRetry: () => void;
  onValuesChange: OnValuesChange;
  preloadedOpportunityNames: Partial<Record<Apollo.Integration, string>>;
  quickStartState: TemplateQuickStartState;
  templateWorkflow: LaunchTemplateWorkflow | null;
  templateWorkflowId: UUID | null;
  showRequiredErrorsToken?: number;
}) {
  const { classes } = useBodyStyles({ isTemplateSelected: false });

  if (!templateWorkflowId || activeStep === 1)
    return <TemplateWorkflowsList templateWorkflowId={templateWorkflowId} />;

  if (loading)
    return (
      <LaunchTemplateBodyFrame>
        <LaunchTemplateBodyLoading />
      </LaunchTemplateBodyFrame>
    );

  if (loadError || !templateWorkflow)
    return (
      <LaunchTemplateBodyFrame>
        <StepLayout title="Unable to load template">
          <Typography color="textSecondary">
            The template details could not be loaded. Try again.
          </Typography>
          <AlineButton color="primary" variant="outlined" onClick={onRetry}>
            Retry
          </AlineButton>
        </StepLayout>
      </LaunchTemplateBodyFrame>
    );

  const shouldShowDocuments = getShouldShowDocumentsSection({
    moreDocumentIds: quickStartState.moreDocumentIds,
    templateWorkflowFiles: templateWorkflow.files,
  });
  const documentsList = templateWorkflow.files.map(({ document }) => ({
    documentId: document.id,
    name: document.name,
  }));

  return (
    <div className={classes.main}>
      <div className={classes.stepsContainer}>
        <StepLayout divider title="Add title">
          <EditableNameInput
            dataCy={Select.workflow.detail.nameInput}
            name={quickStartState.workflowName}
            placeholder="Give your workflow a name..."
            updateName={workflowName => onValuesChange({ workflowName })}
            variant={EditableNameVariant.LargeText}
            wrapperClassName={classes.workflowNameInput}
          />
        </StepLayout>
        {shouldShowDocuments && (
          <AddDocuments
            quickStartState={quickStartState}
            restoreOriginal={(documentId: UUID) => {
              onValuesChange({
                replacementFiles: quickStartState.replacementFiles.filter(
                  f => f.templateId !== documentId
                ),
              });
            }}
            templateWorkflowFiles={templateWorkflow.files}
            onValuesChange={onValuesChange}
          />
        )}
        <AssignRoles
          roleAssignmentMap={quickStartState.roleAssignmentMap}
          workflowPermissions={templateWorkflow.permissions}
          onValuesChange={roleAssignmentMap => onValuesChange({ roleAssignmentMap })}
        />
        <LaunchIntakeFormStep
          intakeFormTemplate={templateWorkflow.intakeFormTemplate}
          quickStartState={quickStartState}
          showRequiredErrorsToken={showRequiredErrorsToken}
          onValuesChange={onValuesChange}
        />
        <LaunchDocumentFieldsStep
          alineFields={getLaunchDocumentFields(templateWorkflow, quickStartState)}
          documentsList={documentsList}
          quickStartState={quickStartState}
          onValuesChange={onValuesChange}
        />
        <PickIntegrationOpportunity
          connectionsToShow={connectionsToShow}
          integrationRecordMap={quickStartState.integrationRecordMap}
          preloadedOpportunityNames={preloadedOpportunityNames}
          onPickOpportunity={(opportunity, action) => {
            switch (action) {
              case 'add':
                return onValuesChange({
                  integrationRecordMap: {
                    ...quickStartState.integrationRecordMap,
                    [opportunity.integration]: opportunity.opportunityId,
                  },
                });
              case 'remove':
                return onValuesChange({
                  integrationRecordMap: omit(
                    [opportunity.integration],
                    quickStartState.integrationRecordMap
                  ),
                });
            }
          }}
        />
      </div>
    </div>
  );
}

function LaunchTemplateBodyFrame({ children }: { children: React.ReactNode }) {
  const { classes } = useBodyStyles({ isTemplateSelected: false });
  return (
    <div className={classes.main}>
      <div className={classes.stepsContainer}>{children}</div>
    </div>
  );
}

function LaunchTemplateBodyLoading() {
  return (
    <>
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} aria-label="Loading template details" role="status">
          <StepLayout title={<Skeleton variant="text" width={120} />}>
            <Skeleton height={24} variant="text" width="70%" />
            <Skeleton height={40} variant="rounded" width="100%" />
          </StepLayout>
        </div>
      ))}
    </>
  );
}
